import {
  Avatar,
  Box,
  Button,
  Grid,
  Group,
  Stack,
  Text,
  Tooltip,
  useMantineTheme
} from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import { useMediaQuery } from '@mantine/hooks'
import { IconRefresh } from '@tabler/icons'
import cardValidator from 'card-validator'
import { useRouter } from 'next/router'
import { Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react'

import * as DisplayData from '@/components/commons/DisplayData'
import { api, errorHandler, isValidTaxDocument, notificationHandler, Yup } from '@/lib/utils'
import {
  MaskedCreditCardNumberMultiple,
  MaskedCreditCardValidDate,
  MaskedTaxDocumentCPFCNPJ
} from '@/lib/utils/masks'
import { CPF_CNPJ_VALIDATION_REGEX } from '@/lib/utils/taxDocument'
import { PaymentMethodOption as PaymentMethodOptionType } from '@/types'

import * as CreditCardFormFields from './Fields'
import { cryptoToolkit } from './utils'

interface Props {
  children?: ReactNode
  canStoreCreditCard?: boolean
  cardOptions: PaymentMethodOptionType[]
  selectedPaymentMethod: any
  onSelectPaymentMethod: Dispatch<SetStateAction<PaymentMethodOptionType | null>>
  onSelectInstallment: (installment: number | null) => void
  onUpdatePayment?: () => void
}

interface FormValues {
  number: string
  holder: string
  validThru: string
  cvc: string
  taxDocument: string
  installments?: number | string
  saveForNextPayments?: boolean
}

export default function PaymentLink({
  canStoreCreditCard = false,
  cardOptions,
  children,
  selectedPaymentMethod,
  onSelectInstallment,
  onSelectPaymentMethod,
  onUpdatePayment
}: Props) {
  // Hooks
  const theme = useMantineTheme()
  const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`)
  const router = useRouter()
  const { orderUid, site: siteSlug } = router.query || {}

  // States
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Constants
  const disabled = isSubmitting

  const errorMessages = {
    number: {
      required: 'Você precisa colocar o número que se encontra no cartão',
      format: 'Digite um número de cartão válido'
    },
    holder: {
      required: 'Preencha com o nome exatamente como impresso no cartão',
      format: 'Informe o nome como está impresso no cartão, sem acentos ou caracteres especiais',
      max: `O nome do titular deve ter no máximo ${CreditCardFormFields.HOLDER_MAX_LENGTH} caracteres. Abrevie os nomes do meio, se necessário`
    },
    validThru: {
      required: 'Informe a data de validade do cartão',
      format: 'Informe uma data válida de vencimento do seu cartão. Ex: MM/AA'
    },
    cvc: { required: 'Informe o código de segurança do cartão' },
    taxDocument: {
      required: 'Necessário informar o CPF/CNPJ do titular do cartão',
      format: 'Insira um documento válido para o CPF/CNPJ'
    },
    installments: { required: 'Selecione o número de parcelas' }
  }

  // Validation schema
  const schema = Yup.object().shape({
    number: Yup.string()
      .test('valid-credit-card', errorMessages.number.format, value => {
        if (!value) return false
        const inputCard = cardValidator.number(value?.replace(/\s/g, ''))
        return inputCard?.isValid
      })
      .required(errorMessages.number.required),
    holder: Yup.string()
      .required(errorMessages.holder.required)
      .test(
        'holder-min-length',
        errorMessages.holder.format,
        value =>
          CreditCardFormFields.normalizeHolder(value).length >=
          CreditCardFormFields.HOLDER_MIN_LENGTH
      )
      .test(
        'holder-max-length',
        errorMessages.holder.max,
        value =>
          CreditCardFormFields.normalizeHolder(value).length <=
          CreditCardFormFields.HOLDER_MAX_LENGTH
      ),
    validThru: Yup.string()
      .matches(/^(0[1-9]|1[0-2])\/?([0-9]{4}|[0-9]{2})$/, errorMessages.validThru.format)
      .test('valid-expiry-date', errorMessages.validThru.format, value => {
        if (!value) return false
        const [expMonth, expYear] = value.split('/')
        const today = new Date()
        const month = today.getMonth() + 1
        const year = today.getFullYear() % 100
        if (Number(expYear) < year) return false
        if (Number(expYear) === year && Number(expMonth) < month) return false
        return true
      })
      .required(errorMessages.validThru.required),
    cvc: Yup.string()
      .required(errorMessages.cvc.required)
      .min(3, errorMessages.cvc.required)
      .max(4, errorMessages.cvc.required),
    taxDocument: Yup.string()
      .matches(CPF_CNPJ_VALIDATION_REGEX, errorMessages.taxDocument.format)
      .test(
        'valid-tax-document',
        errorMessages.taxDocument.format,
        value => !value || isValidTaxDocument(value)
      )
      .required(errorMessages.taxDocument.required),
    installments: Yup.string().required(errorMessages.installments.required),
    saveForNextPayments: Yup.boolean()
  })

  // Mantine form
  const form = useForm<FormValues>({
    validate: yupResolver(schema),
    validateInputOnChange: true,
    validateInputOnBlur: true,
    initialValues: {
      number: '',
      cvc: '',
      holder: '',
      validThru: '',
      taxDocument: '',
      installments: '',
      saveForNextPayments: canStoreCreditCard
    }
  })

  const cardOption = cardOptions
    .find(item => item.paymentMethod?.code === selectedPaymentMethod?.paymentMethod?.code)
    ?.installments?.find?.(item => item.installments === form.values.installments)

  // Actions
  const renderInstallments = () => {
    const inputCard = cardValidator.number(form?.values?.number)
    const method = cardOptions?.find(
      card => card?.paymentMethod?.name === inputCard?.card?.niceType
    )
    return form.isValid('number') ? method?.installments || [] : []
  }

  const handleSubmit = async (newValues: FormValues) => {
    const [expMonth, expYear] = newValues.validThru.split('/')

    const cleanedValues = {
      holder: CreditCardFormFields.normalizeHolder(newValues.holder),
      expYear,
      expMonth,
      number: newValues.number.replace(/\s/g, ''),
      cvc: newValues.cvc,
      taxDocument: newValues.taxDocument
    }

    setIsSubmitting(true)

    const inputCard = cardValidator.number(cleanedValues.number)
    const option = cardOptions?.find(
      card => card?.paymentMethod?.name === inputCard?.card?.niceType
    )
    const { paymentMethod, ...restOption } = option || {}
    const { paymentGateway, ...restPaymentMethod } = paymentMethod || {}
    const toolkitOption = { paymentGateway, ...restOption, ...restPaymentMethod }
    try {
      const encrypted = await cryptoToolkit(cleanedValues, toolkitOption)
      const { pagseguro, iugu, cielo, cognaPay, celcoin, creditCard } = encrypted || {}
      await api
        .post(`/${siteSlug}/public-order/${orderUid}/generate-payment/`, {
          paymentMethodUid: paymentMethod?.uid,
          holder: CreditCardFormFields.normalizeHolder(newValues.holder),
          firstDigits: creditCard?.number.slice(0, 6),
          lastDigits: creditCard?.number.slice(-4),
          expMonth: creditCard?.expMonth,
          expYear: creditCard?.expYear,
          brand: paymentMethod?.brand?.code,
          taxDocument: newValues.taxDocument,
          installments: newValues.installments,
          saveForNextPayments: newValues.saveForNextPayments,
          options: {
            ...(pagseguro?.hash ? { pagseguro } : {}),
            ...(iugu?.hash ? { iugu } : {}),
            ...(cielo?.hash ? { cielo } : {}),
            ...(cognaPay ? { cogna_pay: cognaPay } : {}),
            ...(celcoin?.hash ? { celcoin } : {})
          }
        })
        .then(() => {
          if (onUpdatePayment) onUpdatePayment()
          notificationHandler({
            variant: 'success',
            message: 'Solicitação de cobrança no cartão realizada com sucesso'
          })
        })
        .catch(error => {
          const errors = errorHandler(error?.response?.data)
          notificationHandler({
            variant: 'error',
            message:
              `${errors?.messages?.[0]}` ||
              'Erro ao gerar pagamento para o método de pagamento escolhido. Entre em contato com o suporte ou tente novamente mais tarde.'
          })
        })
    } catch (error: any) {
      const errors = errorHandler(error?.response?.data)
      notificationHandler({
        variant: 'error',
        message:
          `${errors?.messages?.[0]}` ||
          'Erro ao criptografar os dados do cartão. Entre em contato com o suporte ou tente novamente mais tarde.'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const checkCardPaymentMethod = () => {
    const input = cardValidator.number(form.values.number)
    const inputCard: any = cardOptions?.find(
      card => card?.paymentMethod?.name === input?.card?.niceType
    )
    onSelectPaymentMethod?.(inputCard)
  }

  useEffect(() => {
    if (form.values.number && !selectedPaymentMethod) checkCardPaymentMethod()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.number, selectedPaymentMethod])

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Grid>
        <Grid.Col span={12} xs={children ? 7 : 12}>
          <Stack>
            <Box>
              <Text size="sm" color="dimmed">
                Você pode pagar com:
              </Text>
              <Group spacing="sm" mt="xs">
                {cardOptions?.map((option: any) => (
                  <Tooltip
                    key={option.paymentMethod?.brand?.name}
                    label={option.paymentMethod?.brand?.name}
                    withArrow>
                    <Avatar
                      size={isXs ? 'sm' : 'md'}
                      src={option.paymentMethod?.brand?.logo}
                      alt={option.paymentMethod?.brand?.name}
                    />
                  </Tooltip>
                ))}
              </Group>
            </Box>
            <CreditCardFormFields.NumberField
              inputProps={{
                ...form.getInputProps('number'),
                component: MaskedCreditCardNumberMultiple,
                disabled
              }}
              form={form}
              cardOptions={cardOptions}
              callback={(data: any) => onSelectPaymentMethod(data)}
            />
            <CreditCardFormFields.NameField
              inputProps={{
                ...form.getInputProps('holder'),
                disabled
              }}
            />
            <Group spacing="sm" noWrap align="flex-start" grow>
              <CreditCardFormFields.ValidThruField
                inputProps={{
                  ...form.getInputProps('validThru'),
                  component: MaskedCreditCardValidDate,
                  disabled
                }}
              />
              <CreditCardFormFields.CVVField
                inputProps={{ ...form.getInputProps('cvc'), disabled }}
              />
            </Group>
            <CreditCardFormFields.TaxDocumentField
              inputProps={{
                ...form.getInputProps('taxDocument'),
                component: MaskedTaxDocumentCPFCNPJ,
                disabled
              }}
            />
            {!!form.isValid('number') && (
              <CreditCardFormFields.InstallmentsSelectField
                selected={form.values.installments}
                installments={renderInstallments()}
                inputProps={{
                  ...form.getInputProps('installments'),
                  size: 'md',
                  disabled: disabled || !form.isValid('number'),
                  onChange: (option: any) => {
                    form.setFieldValue('installments', option)
                    onSelectInstallment(Number(option))
                  }
                }}
              />
            )}
            {canStoreCreditCard && (
              <CreditCardFormFields.StoredCheckbox
                inputProps={{
                  label:
                    'Salvar cartão para débito automático nas próximas cobranças da matrícula do(a) aluno(a)',
                  checked: form.values.saveForNextPayments,
                  ...form.getInputProps('saveForNextPayments', { type: 'checkbox' }),
                  disabled
                }}
              />
            )}
            {!!form.values.installments && !!cardOption && (
              <Text fw="bold" fz="xl">
                Total do pagamento:{' '}
                <DisplayData.CurrencyField value={Number(cardOption.total) || 0} />
              </Text>
            )}
          </Stack>
        </Grid.Col>
        <Grid.Col span={12} xs={5} hidden={!children}>
          {children}
        </Grid.Col>
        <Grid.Col span={12}>
          <Group position={isXs ? 'center' : 'left'}>
            <Button
              type="submit"
              color="green"
              fullWidth={isXs}
              disabled={!form.isValid() || !form.isTouched() || isSubmitting}>
              Finalizar compra
            </Button>

            {!!onUpdatePayment && (
              <Button
                fullWidth={isXs}
                variant="subtle"
                disabled={isSubmitting}
                leftIcon={<IconRefresh />}
                onClick={onUpdatePayment}>
                Atualizar pagamento
              </Button>
            )}
          </Group>
        </Grid.Col>
      </Grid>
    </form>
  )
}
